/**
 * @packageDocumentation
 * @module hooks/useResizeObserver
 */
import { useRef, useEffect, useCallback } from 'react';
import { ResizeObserver, ResizeObserverEntry } from '@juggle/resize-observer';
import { ResizeObserverCallback } from '@juggle/resize-observer/lib/ResizeObserverCallback';
import { ResizeObserverOptions } from '@juggle/resize-observer/lib/ResizeObserverOptions';

/**
 * Returns an instance of `@juggle/resize-observer`. If you aren't sure how to use this,
 * you may want to look at `useResizeObserverRef` instead.
 *
 * @param onResize the function to call when a resize is detected.
 *
 * @see useResizeObserverRef
 * @see https://github.com/juggle/resize-observer
 */
export function useResizeObserver(onResize: ResizeObserverCallback): ResizeObserver {
  const refOnResize = useRef<ResizeObserverCallback>(onResize);
  useEffect(() => {
    refOnResize.current = onResize;
  });

  const refResizeObserver = useRef<ResizeObserver>();
  useEffect(
    // cleanup on component dismount
    () => () => {
      refResizeObserver.current?.disconnect();
      refResizeObserver.current = undefined;
    },
    []
  );

  if (refResizeObserver.current === undefined) {
    // NOTE: creating this in render is fine, but if users try to call observe on this during first render,
    //     | it can leak event handlers and the resize observer instance in concurrent mode.
    refResizeObserver.current = new ResizeObserver(function __onResize(entries, observer) {
      const callback = refOnResize.current;
      callback(entries, observer);
    });
  }
  return refResizeObserver.current;
}

/**
 * Creates a DOM ref that has a resize observer attached.
 *
 * @param onResize the function to call when a resize is detected
 * @param options options to pass to the ResizeObserver
 *
 * @returns A tuple with a function ref and a getter for the current ref value.
 *
 * @example
 * ```jsx
 * function MyC() {
 *   const [ref] = useResizeObserverRef((entry) => {
 *     console.log(`resized: ${entry.borderBoxSize[0].inlineSize}`);
 *   });
 *
 *   return <div ref={ref} />;
 * }
 * ```
 *
 * @see https://github.com/juggle/resize-observer
 */
export function useResizeObserverRef<T extends Element>(
  onResize: (entry: ResizeObserverEntry) => void,
  options?: ResizeObserverOptions
): [(instance: T | null) => void, () => T | null] {
  const resizeObserver = useResizeObserver(function ([entry]) {
    const timer = setTimeout(
      (entry) => {
        if (ref.current) {
          onResize(entry);
        }
        clearTimeout(timer);
      },
      0,
      entry
    );
  });

  const box = options?.box;

  const ref = useRef<T | null>(null);
  return [
    useCallback(
      (instance: T | null): void => {
        if (instance) {
          if (instance !== ref.current && ref.current) {
            resizeObserver.unobserve(ref.current);
          }
          ref.current = instance;
          resizeObserver.observe(ref.current, { box });
        } else {
          if (ref.current) {
            resizeObserver.unobserve(ref.current);
          }
          ref.current = null;
        }
      },
      [resizeObserver, box]
    ),
    useCallback(() => ref.current, []),
  ];
}
